import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import useScrollAnimation from '../../hooks/useScrollAnimation';

const HomePage = () => {
  const navigate = useNavigate();
  useScrollAnimation();

  const yogurtFlavors = [
    { name: 'Banana', image: '/images/products/banana-range.png' },
    { name: 'Strawberry', image: '/images/products/strawberry-range.png' },
    { name: 'Pineapple', image: '/images/products/pineapple-range.png' },
    { name: 'Orange', image: '/images/products/orange-range.png' },
    { name: 'Marula', image: '/images/products/marula-range.png' }
  ];

  const retailers = [
    { name: 'SHOPRITE', logo: '/images/retailers/shoprite.png' },
    { name: 'SANA', logo: '/images/retailers/sana.png' },
    { name: 'CHIPIKU', logo: '/images/retailers/chipiku.png' },
    { name: 'GAME', logo: '/images/retailers/game.jpg' },
    { name: 'Local Vendors', logo: '/images/retailers/retailer.png' }
  ];

  const features = [
    { icon: 'fas fa-leaf', title: '100% Natural', description: 'Made with real fruit and full-cream milk.' },
    { icon: 'fas fa-award', title: 'MBS Certified', description: 'Quality guaranteed by the Malawi Bureau of Standards.' },
    { icon: 'fas fa-heart', title: 'Loved Nationwide', description: 'A trusted choice for Malawian families.' },
    { icon: 'fas fa-recycle', title: 'Eco-Friendly', description: 'Mindful production and packaging.' }
  ];

  // Navigation handler
  const handleNavigate = (path) => {
    navigate(`/${path}`);
  };

  return (
    <>
      {/* SEO Meta */}
      <Helmet>
        <title>Riverton Dairy - Malawian Yogurt, 100% Natural & Delicious</title>
        <meta name="description" content="Riverton Dairy produces rich and creamy yogurt in Malawi using real fruit and full-cream milk. Explore our flavors and find our products nationwide." />
        <meta name="keywords" content="Riverton Dairy, Malawian Yogurt, Natural Yogurt, Full Cream Milk, Flavored Yogurt, Lilongwe, MBS Certified" />
        <meta name="author" content="Riverton Dairy" />

        {/* Open Graph */}
        <meta property="og:title" content="Riverton Dairy - Malawian Yogurt, 100% Natural & Delicious" />
        <meta property="og:description" content="Discover Riverton Dairy's rich and creamy yogurts made in Malawi using real fruit and full-cream milk. Loved nationwide." />
        <meta property="og:image" content="/images/hero/hero-bg.png" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
      </Helmet>

      {/* HERO */}
      <section 
        className="hero scroll-animate"
        style={{ backgroundImage: `url('/images/hero/hero-bg.png')` }}
      >
        <div className="container">
          <div className="hero-content">
            <h1>It's Riverton, <span>It's Delicious</span></h1>
            <h2 className="sub-heading">Proudly Malawian. Proudly Natural.</h2>
            <p>
              Enjoy Riverton's rich, smooth yogurts made in Lilongwe using local milk 
              and real fruit. Fresh, flavourful, and MBS certified.
            </p>
            <div className="hero-buttons">
              <a onClick={() => handleNavigate('products')} className="btn btn-primary" style={{cursor: 'pointer'}}>
                <i className="fas fa-shopping-basket"></i> Explore Yogurts
              </a>
              <a onClick={() => handleNavigate('about')} className="btn btn-secondary" style={{cursor: 'pointer'}}>
                <i className="fas fa-play-circle"></i> Our Story
              </a>
            </div>
            <div className="hero-features">
              <div className="feature-item"><i className="fas fa-check-circle"></i>MBS Certified</div>
              <div className="feature-item"><i className="fas fa-check-circle"></i>100% Natural</div>
              <div className="feature-item"><i className="fas fa-check-circle"></i>Local Ingredients</div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="features-section scroll-animate">
        <div className="container">
          <div className="features-grid">
            {features.map((feature, i) => (
              <div key={i} className="feature-card">
                <div className="feature-icon"><i className={feature.icon}></i></div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FLAVOURS */}
      <section className="product-categories scroll-animate">
        <div className="container">
          <div className="section-header">
            <h2>Our Yogurt Flavours</h2>
            <p>Available in 250ml, 500ml and 1L bottles.</p>
          </div>
          <div className="categories-grid">
            {yogurtFlavors.map((flavor, idx) => (
              <div key={idx} className="category-card" style={{ animationDelay: `${idx * 0.1}s` }}>
                <div 
                  className="category-image"
                  style={{ backgroundImage: `url('${flavor.image}')` }}
                >
                  <div className="category-overlay">
                    <a onClick={() => handleNavigate('products')} className="btn btn-primary" style={{cursor: 'pointer'}}>
                      View Products
                    </a>
                  </div>
                </div>
                <div className="category-content">
                  <h3>{flavor.name} Yogurt</h3>
                  <p>
                    Smooth, creamy yogurt made with real {flavor.name.toLowerCase()} flavour. 
                    Deliciously Malawian.
                  </p>
                  <div className="category-features">
                    <span className="feature-tag">Real Fruit</span>
                    <span className="feature-tag">Full Cream Milk</span>
                    <span className="feature-tag">MBS Certified</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="section-cta">
            <a onClick={() => handleNavigate('products')} className="btn btn-primary btn-large" style={{cursor: 'pointer'}}>
              <i className="fas fa-arrow-right"></i> Full Yogurt Range
            </a>
          </div>
        </div>
      </section>

      {/* RETAILERS */}
      <section className="retailers-section scroll-animate">
        <div className="container">
          <div className="section-header">
            <h2>Available Nationwide</h2>
            <p>Find Riverton Yogurt at leading retailers across Malawi.</p>
          </div>
          <div className="retailers-grid">
            {retailers.map((retailer, idx) => (
              <div key={idx} className="retailer-logo" style={{ animationDelay: `${idx * 0.1}s` }}>
                <div 
                  className="retailer-image" 
                  style={{ backgroundImage: `url('${retailer.logo}')` }}
                ></div>
                <span className="retailer-name">{retailer.name}</span>
              </div>
            ))}
          </div>
          <div className="retailers-cta">
            <p>
              Can't find our products? <a onClick={() => handleNavigate('contact')} style={{cursor: 'pointer'}}>Contact us</a> for assistance.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section scroll-animate">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Taste Riverton?</h2>
            <p>Rich, creamy, and flavourful — Malawi's favourite yogurt.</p>
            <div className="cta-buttons">
              <a onClick={() => handleNavigate('products')} className="btn btn-primary btn-large" style={{cursor: 'pointer'}}>
                <i className="fas fa-shopping-cart"></i> View Products
              </a>
              <a onClick={() => handleNavigate('contact')} className="btn btn-secondary" style={{cursor: 'pointer'}}>
                <i className="fas fa-store"></i> Find a Store
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;